import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, Percent, Clock, ShieldCheck, Calculator, Phone, ArrowRight, Check, FileText, Sparkles, Building2, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { buildWhatsAppLink } from "@/components/WhatsAppFab";

const PSU_BANKS = [
  { name: "State Bank of India", short: "SBI", color: "from-blue-700 to-indigo-800", note: "Largest PSB · Lowest floor rate" },
  { name: "Bank of Baroda", short: "BoB", color: "from-orange-600 to-red-700", note: "Liberal LTV for salaried" },
  { name: "Punjab National Bank", short: "PNB", color: "from-amber-600 to-orange-700", note: "Long tenure up to 30 yrs" },
  { name: "Canara Bank", short: "Canara", color: "from-yellow-600 to-amber-700", note: "Senior-citizen schemes" },
  { name: "Union Bank of India", short: "Union", color: "from-rose-700 to-red-800", note: "Reduced processing fees" },
  { name: "Bank of India", short: "BoI", color: "from-orange-500 to-amber-600", note: "Quick digital sanctions" },
  { name: "Indian Bank", short: "IB", color: "from-indigo-700 to-blue-800", note: "Govt-employee preference" },
  { name: "Bank of Maharashtra", short: "BoM", color: "from-amber-700 to-yellow-800", note: "Maharashtra HQ network" },
  { name: "Central Bank of India", short: "CBI", color: "from-emerald-700 to-teal-800", note: "First-time buyer schemes" },
  { name: "UCO Bank", short: "UCO", color: "from-blue-600 to-cyan-700", note: "Concessional NRI rates" },
  { name: "IDBI Bank", short: "IDBI", color: "from-green-700 to-emerald-800", note: "Project-tied approvals" },
  { name: "Indian Overseas Bank", short: "IOB", color: "from-amber-800 to-orange-900", note: "South-India strong network" },
];

const PRIVATE_BANKS = [
  { name: "HDFC Bank", short: "HDFC", color: "from-blue-800 to-indigo-900", note: "Industry-fastest sanctions" },
  { name: "ICICI Bank", short: "ICICI", color: "from-orange-600 to-red-700", note: "Step-up & balance-transfer" },
  { name: "Axis Bank", short: "Axis", color: "from-rose-700 to-pink-800", note: "Doctors / CA preferred" },
  { name: "Kotak Mahindra Bank", short: "Kotak", color: "from-red-700 to-rose-800", note: "Top-up for renovation" },
  { name: "IndusInd Bank", short: "IndusInd", color: "from-amber-700 to-orange-800", note: "NRI 80% LTV" },
  { name: "Yes Bank", short: "Yes", color: "from-blue-700 to-indigo-800", note: "Self-employed friendly" },
  { name: "Federal Bank", short: "Federal", color: "from-amber-600 to-yellow-700", note: "GCC-NRI specialty" },
  { name: "RBL Bank", short: "RBL", color: "from-red-800 to-rose-900", note: "Salaried express loans" },
];

const HFCs = [
  { name: "LIC Housing Finance", short: "LIC HFL", color: "from-amber-700 to-yellow-800", note: "Lowest rates · longest tenor" },
  { name: "Bajaj Housing Finance", short: "Bajaj HF", color: "from-indigo-700 to-blue-800", note: "Up to 90% LTV" },
  { name: "PNB Housing Finance", short: "PNB HF", color: "from-orange-700 to-red-800", note: "Builder tie-up sanctions" },
  { name: "Tata Capital Housing", short: "Tata HF", color: "from-blue-800 to-slate-900", note: "Premium-segment loans" },
];

const FEATURES = [
  { icon: Percent, title: "Best-in-market rates", body: "We negotiate across 20+ lenders and bring you the lowest applicable rate for your profile." },
  { icon: Clock, title: "Sanctions in 72 hours", body: "Pre-approved digital sanction letters within 3 working days of complete documentation." },
  { icon: ShieldCheck, title: "RERA-aligned advisory", body: "We only fund RERA-registered projects with valid title and approvals — your equity is protected." },
  { icon: Banknote, title: "Up to 90% funding", body: "Higher LTVs for salaried borrowers, NRIs and pre-approved developer projects." },
  { icon: FileText, title: "Document checklist", body: "One curated checklist by lender — no back-and-forth. We pre-validate before bank submission." },
  { icon: Sparkles, title: "Special schemes", body: "Doctors, CAs, lawyers, women borrowers, and CGHS / military personnel — concessional rates." },
];

const STEPS = [
  { n: "01", t: "Profile Assessment", d: "30-min call to map your income, CIBIL, age, employment and target property." },
  { n: "02", t: "Lender Shortlist", d: "Top 3 lenders matched to your profile with rate-sheet, fees and LTV comparison." },
  { n: "03", t: "Documentation", d: "End-to-end help with KYC, ITR, salary slips, property papers and legal vetting." },
  { n: "04", t: "Sanction & Disbursement", d: "Sanction letter, agreement signing, registration coordination and disbursement to seller." },
];

function BankCard({ bank, idx }) {
  return (
    <div
      data-testid={`bank-card-${bank.short.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative bg-white border border-slate-200 hover:border-amber-200 transition-colors overflow-hidden"
      style={{ animation: `fadeUp 400ms ease both ${idx * 30}ms` }}
    >
      <div className={`h-24 bg-gradient-to-br ${bank.color} flex items-center justify-center relative`}>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,white_0%,transparent_50%)]" />
        <div className="font-serif text-3xl text-white tracking-wide drop-shadow-md">{bank.short}</div>
      </div>
      <div className="p-5">
        <div className="text-sm font-medium text-slate-900">{bank.name}</div>
        <div className="text-xs text-slate-600 mt-2 leading-relaxed font-light">{bank.note}</div>
      </div>
    </div>
  );
}

export default function HomeLoans() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "I'd like to discuss a home-loan option." });
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share name, email and phone.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiry", { ...form, message: `[Home Loan] ${form.message}`, consent: true });
      toast.success("Thank you. Our loan desk will reach out within 4 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-loans-page" className="pb-32">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=2400"
            alt="Home loan partners"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-16">
          <div className="overline mb-5 flex items-center gap-2"><Banknote className="w-3.5 h-3.5" /> Home Loan Consultation</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 font-light leading-[1.05] max-w-4xl">
            One application. <em className="text-[#D4A437] not-italic">24+ banks.</em><br />
            The lowest rate, locked.
          </h1>
          <p className="text-slate-700 text-lg mt-8 max-w-2xl font-light leading-relaxed">
            Tied up with every major nationalised bank, leading private banks, and India&apos;s top housing-finance
            companies — we shop the rate, you sign the cheque. Sanctions in 72 hours.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#hl-contact"
              data-testid="hl-cta-talk"
              className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-7 py-3.5 transition-colors"
            >
              Get a Sanction <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/emi-calculator"
              data-testid="hl-cta-emi"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#D4A437] hover:text-[#D4A437] text-slate-800 px-7 py-3.5 transition-colors"
            >
              <Calculator className="w-4 h-4" /> Calculate EMI
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
        {[
          { k: "24+", v: "Banks & HFCs" },
          { k: "8.35%*", v: "Floor Rate" },
          { k: "90%", v: "Max LTV" },
          { k: "72 hrs", v: "Sanction TAT" },
        ].map((s) => (
          <div key={s.v} className="bg-white p-8 text-center" data-testid={`hl-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="font-serif text-4xl text-[#D4A437]">{s.k}</div>
            <div className="overline mt-2">{s.v}</div>
          </div>
        ))}
      </section>
      <p className="max-w-7xl mx-auto px-6 md:px-12 mt-3 text-[10px] text-slate-600">*Indicative floor rate · subject to RBI repo, profile and lender norms.</p>

      {/* PSU banks */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Public-sector lenders</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 mb-2">All 12 nationalised banks.</h2>
        <p className="text-slate-600 mb-12 max-w-2xl font-light">India&apos;s safest credit pool — government-backed lenders with the longest tenors and lowest floor rates.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {PSU_BANKS.map((b, i) => (<BankCard key={b.short} bank={b} idx={i} />))}
        </div>
      </section>

      {/* Private banks */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="overline mb-4">Private banks</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 mb-2">India&apos;s leading private lenders.</h2>
        <p className="text-slate-600 mb-12 max-w-2xl font-light">Faster approvals, sharper customer service, and specialised products for self-employed and NRI borrowers.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {PRIVATE_BANKS.map((b, i) => (<BankCard key={b.short} bank={b} idx={i} />))}
        </div>
      </section>

      {/* HFCs */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="overline mb-4">Housing finance companies</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-slate-900 mb-2">Specialist HFCs for higher LTV.</h2>
        <p className="text-slate-600 mb-12 max-w-2xl font-light">When banks say no — HFCs say yes. Up to 90% LTV with project-tie-up sanctions.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
          {HFCs.map((b, i) => (<BankCard key={b.short} bank={b} idx={i} />))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">Why borrow through OC</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-12 max-w-3xl">
          The shortest path from intent to keys.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100 border border-slate-100">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white p-8" data-testid={`hl-feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <f.icon className="w-6 h-6 text-[#D4A437] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-lg text-slate-900 mb-3">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">Process</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-12 max-w-3xl">From application to disbursement — typically 2 weeks.</h2>
        <div className="grid md:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white p-8" data-testid={`hl-step-${s.n}`}>
              <div className="overline mb-4">{s.n}</div>
              <h3 className="font-serif text-lg text-slate-900 mb-3">{s.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-light">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section
        id="hl-contact"
        className="max-w-7xl mx-auto px-6 md:px-12 mt-32 bg-amber-50/40 border border-slate-200 p-10 md:p-16 grid md:grid-cols-12 gap-12"
      >
        <div className="md:col-span-5">
          <div className="overline mb-4">Loan Desk</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">Get a free eligibility check.</h2>
          <p className="text-slate-600 mt-6 font-light leading-relaxed">
            Our loan desk will revert with three best-fit lenders, indicative rates, EMIs and a checklist within 4 hours.
            No obligation — and no impact on your CIBIL during eligibility check.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4A437]" /> +91 522 4000 800</li>
            <li className="flex items-center gap-3"><Banknote className="w-4 h-4 text-[#D4A437]" /> loans@ocinfraventures.com</li>
            <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D4A437] mt-0.5" /> Indicative rates only — final terms per lender&apos;s credit policy.</li>
          </ul>
          <a
            href={buildWhatsAppLink("Hi OC Infraventures, I'd like to discuss a home-loan option. Please share the lowest available rate for my profile.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hl-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-6 py-3 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </div>
        <form onSubmit={submit} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="hl-contact-form">
          <div><label className="overline block mb-2">Full Name</label>
            <input data-testid="hl-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div><label className="overline block mb-2">Email</label>
            <input type="email" data-testid="hl-input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Phone</label>
            <input data-testid="hl-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
              placeholder="+91 9XXX XXX XXX"
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Loan amount &amp; property city (optional)</label>
            <textarea data-testid="hl-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2">
            <ConsentCheckbox checked={consent} onChange={setConsent} testId="inq-consent-homeloan" />
            <button type="submit" disabled={submitting || !consent} data-testid="hl-submit-btn"
              className="w-full sm:w-auto mt-5 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-8 py-3.5 transition-colors disabled:opacity-50">
              {submitting ? "Sending..." : "Request Eligibility Check →"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
